import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '../css/Solicitud.css';
import Dropdown from './Dropdown/Dropdown';
import DropdownItem from './DropdownItem/DropdownItem';
import { getSolicitud, updateTaller, updateDescripcion, updateUsuario, resetSolicitud } from '../redux/actions/solicitud';
import { listTalleres } from '../redux/actions/talleres';
import { splitTalleres, joinTalleres } from '../utils';
import { MAX_TALLERES_SELECCION } from '../consts';

const SolicitudForm = ({ idPedido, isEditMode }) => {
  const dispatch = useDispatch();
  const { taller: tller, descripcion: descrip } = useSelector((state) => state.Solicitud);
  const { user: usuario } = useSelector((state) => state.Auth);
  const { talleres, loading, error } = useSelector((state) => state.Taller);

  const [selectedTalleres, setSelectedTalleres] = useState(
    splitTalleres(sessionStorage.getItem('selectedTaller'))
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [descripcion, setDescripcion] = useState(sessionStorage.getItem('descripcionTaller') || descrip || '');
  const [focused, setFocused] = useState(false);
  const [tallerError, setTallerError] = useState('');

  const buttonText = useMemo(() => {
    if (selectedTalleres.length === 0) return 'Seleccione uno o más Talleres';
    if (selectedTalleres.length === 1) return selectedTalleres[0];
    return `${selectedTalleres.length} talleres seleccionados`;
  }, [selectedTalleres]);

  const filteredTalleres = useMemo(() => {
    if (!talleres) return [];
    const term = searchTerm.trim().toLowerCase();
    const list = !term
      ? [...talleres]
      : talleres.filter((item) =>
          (item.nombre || '').toLowerCase().includes(term)
        );
    return list.sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
    );
  }, [talleres, searchTerm]);

  useEffect(() => {
    dispatch(listTalleres());
    if (isEditMode && idPedido) {
      dispatch(getSolicitud(idPedido));
    } else {
      // Clear form for new pedido
      dispatch(resetSolicitud());
      setSelectedTalleres([]);
      setDescripcion('');
      setSearchTerm('');
      setTallerError('');
      sessionStorage.removeItem('selectedTaller');
      sessionStorage.removeItem('descripcionTaller');
    }
  }, [dispatch, idPedido, isEditMode]);

  useEffect(() => {
    if (usuario && usuario !== null) {
      dispatch(updateUsuario(usuario));
    }
  }, [dispatch, usuario]);

  // Sincroniza el estado local cuando llega el detalle de un pedido en modo edición.
  useEffect(() => {
    if (descrip) {
      setDescripcion(descrip);
      sessionStorage.setItem('descripcionTaller', descrip);
    }
    if (tller) {
      const nombres = splitTalleres(tller);
      setSelectedTalleres((prev) => {
        const prevJoined = joinTalleres(prev);
        const nextJoined = joinTalleres(nombres);
        return prevJoined === nextJoined ? prev : nombres;
      });
      sessionStorage.setItem('selectedTaller', joinTalleres(nombres));
    }
  }, [descrip, tller]);

  const handleToggleTaller = (nombre) => {
    const name = String(nombre).trim();
    if (!name) return;

    setTallerError('');

    const isSelected = selectedTalleres.includes(name);
    let next;
    if (isSelected) {
      next = selectedTalleres.filter((n) => n !== name);
    } else {
      if (selectedTalleres.length >= MAX_TALLERES_SELECCION) {
        return; // límite alcanzado
      }
      next = [...selectedTalleres, name];
    }

    setSelectedTalleres(next);
    const joined = joinTalleres(next);
    sessionStorage.setItem('selectedTaller', joined);
    dispatch(updateTaller(joined));
  };

  const handleClearTalleres = () => {
    setSelectedTalleres([]);
    setTallerError('');
    sessionStorage.removeItem('selectedTaller');
    dispatch(updateTaller(''));
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleDescripcionChange = (value) => {
    setDescripcion(value);
    sessionStorage.setItem('descripcionTaller', value);
    dispatch(updateDescripcion(value));
  };

  const handleSiguienteClick = (e) => {
    const errorSpan = document.querySelector('.error-message-taller');
    if (selectedTalleres.length === 0) {
      e.preventDefault();
      setTallerError('Debe seleccionar al menos un taller');
      if (errorSpan) {
        errorSpan.textContent = 'Debe seleccionar al menos un taller';
        errorSpan.style.display = 'block';
      }
    }
  };

  return (
    <div className="container">
      <form action="" className="bg-white text-center ">
        <h3>Crear solicitud</h3>
        <div className="content" focused={focused.toString()}>
          <Dropdown
            buttonText={buttonText}
            closeOnClick={false}
            content={
              <>
                <div className="taller-search">
                  <input
                    type="text"
                    className="taller-search-input"
                    placeholder="Buscar taller..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                {loading && <DropdownItem>Cargando...</DropdownItem>}
                {error && <DropdownItem>Error: {error}</DropdownItem>}
                {!loading && !error && filteredTalleres.length === 0 && (
                  <DropdownItem>Sin resultados</DropdownItem>
                )}
                {!loading && !error && filteredTalleres.map((item) => {
                  const isSelected = selectedTalleres.includes(item.nombre);
                  return (
                    <DropdownItem
                      key={item.id}
                      onclick={() => handleToggleTaller(item.nombre)}
                    >
                      <span className={`taller-check ${isSelected ? 'taller-check-selected' : ''}`}>
                        {isSelected ? '✓' : ''}
                      </span>
                      <span className={isSelected ? 'taller-name-selected' : 'taller-name'}>
                        {item.nombre}
                      </span>
                    </DropdownItem>
                  );
                })}
                {!loading && !error && selectedTalleres.length > 0 && (
                  <DropdownItem onclick={handleClearTalleres}>
                    <span className="taller-clear">Limpiar selección</span>
                  </DropdownItem>
                )}
                {selectedTalleres.length >= MAX_TALLERES_SELECCION && (
                  <DropdownItem>
                    <span className="taller-max-warning">
                      Máximo {MAX_TALLERES_SELECCION} talleres por solicitud
                    </span>
                  </DropdownItem>
                )}
              </>
            }
            error={error}
          />
        </div>
        <span className={`error-message error-message-taller ${tallerError ? 'error-message-visible' : ''}`}>
          {tallerError || 'Debe seleccionar al menos un taller'}
        </span>
        <input
          className="my-3"
          type="text"
          placeholder="Descripción"
          value={descripcion}
          required
          onBlur={handleFocus}
          focused={focused.toString()}
          onChange={(e) => handleDescripcionChange(e.target.value)}
        />
        <span className='error-message'>La descripción no puede estar vacía</span>
        <div className="mt-3 d-flex justify-content-between container-buttons">
          <Link to="/landing"><button className='button'>Volver</button></Link>
          <Link to={`/fechas/`}>
            <button
              className='button'
              onClick={handleSiguienteClick}>
              Siguiente
            </button>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default SolicitudForm;